// src/pages/ContactDetailPage.tsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { contactsApi, activitiesApi, accountsApi } from '../api';
import { baseClient } from '../api/baseClient';
import {
  Box, Typography, CircularProgress, Alert, Paper, Grid,
  Container, Divider, Button, Avatar, Chip, Card, CardContent,
  List, ListItem, ListItemText, ListItemIcon, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Breadcrumbs, Link, Tab, Tabs, Select, MenuItem, FormControl, InputLabel,
  TextareaAutosize
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon, Edit as EditIcon, Email as EmailIcon,
  Person as PersonIcon, Business as BusinessIcon, Phone as PhoneIcon,
  CalendarToday as CalendarIcon, TrendingUp as TrendingUpIcon,
  Psychology as PsychologyIcon, Assignment as AssignmentIcon,
  Note as NoteIcon, Add as AddIcon, Save as SaveIcon
} from '@mui/icons-material';
import type { ContactReadWithInsights, ContactReadWithAccount, Activity, AIInsight } from '../types/api';
import InsightsGenerator from '../components/ai/InsightsGenerator';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contact-tabpanel-${index}`}
      aria-labelledby={`contact-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `contact-tab-${index}`,
    'aria-controls': `contact-tabpanel-${index}`,
  };
}

export function ContactDetailPage() {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    title: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [newNote, setNewNote] = useState('');
  const [noteSubject, setNoteSubject] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');

  // Fetch contact details with insights
  const { data: contact, isLoading, isError, error } = useQuery<ContactReadWithInsights>({
    queryKey: ['contacts', contactId, 'insights'],
    queryFn: async () => {
      if (!contactId) throw new Error('Contact ID is required');
      return await contactsApi.getContactWithInsights(contactId);
    },
    enabled: !!contactId
  });

  // Fetch contact activities
  const { data: activities = [] } = useQuery<Activity[]>({
    queryKey: ['activities', 'contact', contactId],
    queryFn: async () => {
      if (!contactId) return [];
      try {
        const response = await activitiesApi.getActivitiesByContact(contactId);
        return response.data || [];
      } catch (error) {
        console.error('Error fetching contact activities:', error);
        return [];
      }
    },
    enabled: !!contactId
  });

  // Fetch accounts for the Connect Account dialog
  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      return await accountsApi.getAccounts();
    }
  });

  // Fetch account details for the current contact
  const { data: contactAccount } = useQuery({
    queryKey: ['account', contact?.account_id],
    queryFn: async () => {
      if (!contact?.account_id) return null;
      return await accountsApi.getAccount(contact.account_id);
    },
    enabled: !!contact?.account_id
  });

  // Update contact mutation
  const updateContactMutation = useMutation({
    mutationFn: async (updateData: any) => {
      if (!contactId) throw new Error('Contact ID is required');
      return await contactsApi.updateContact(contactId, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', contactId] });
      setEditDialogOpen(false);
    },
    onError: (error: any) => {
      console.error('Error updating contact:', error);
    }
  });

  // Add note mutation (using contact notes field for now)
  const addNoteMutation = useMutation({
    mutationFn: async (noteData: { subject: string; content: string }) => {
      if (!contactId) throw new Error('Contact ID is required');
      const currentNotes = contact?.notes || '';
      const timestamp = new Date().toLocaleString();
      const newNoteText = `[${timestamp}] ${noteData.subject}: ${noteData.content}`;
      const updatedNotes = currentNotes ? `${currentNotes}\n\n${newNoteText}` : newNoteText;

      return await contactsApi.updateContact(contactId, { notes: updatedNotes });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', contactId] });
      setNotesDialogOpen(false);
      setNewNote('');
      setNoteSubject('');
    },
    onError: (error: any) => {
      console.error('Error adding note:', error);
    }
  });

  // Connect account mutation
  const connectAccountMutation = useMutation({
    mutationFn: async (accountId: string) => {
      if (!contactId) throw new Error('Contact ID is required');
      return await contactsApi.updateContact(contactId, { account_id: accountId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts', contactId] });
      queryClient.invalidateQueries({ queryKey: ['account'] }); // Invalidate all account queries
      setAccountDialogOpen(false);
      setSelectedAccountId('');
    },
    onError: (error: any) => {
      console.error('Error connecting account:', error);
    }
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditClick = () => {
    if (contact) {
      setEditFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        title: contact.title || '',
        email: contact.email,
        phone: contact.phone || '',
        notes: contact.notes || ''
      });
      setEditDialogOpen(true);
    }
  };

  const handleEditSubmit = () => {
    updateContactMutation.mutate(editFormData);
  };

  const handleAddNote = () => {
    if (noteSubject.trim() && newNote.trim()) {
      addNoteMutation.mutate({
        subject: noteSubject.trim(),
        content: newNote.trim()
      });
    }
  };

  const handleConnectAccount = () => {
    if (selectedAccountId) {
      connectAccountMutation.mutate(selectedAccountId);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !contact) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load contact details. Please try again.
        </Alert>
        <Button onClick={() => navigate('/contacts')} startIcon={<ArrowBackIcon />}>
          Back to Contacts
        </Button>
      </Container>
    );
  }

  const getContactDisplayName = (): string => {
    const firstName = contact.first_name || '';
    const lastName = contact.last_name || '';
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    return contact.email;
  };

  const getContactInitials = (): string => {
    const firstName = contact.first_name || '';
    const lastName = contact.last_name || '';
    if (firstName || lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    return contact.email.charAt(0).toUpperCase();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/contacts" underline="hover">
          Contacts
        </Link>
        <Typography color="text.primary">{getContactDisplayName()}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
              {getContactInitials()}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                {getContactDisplayName()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body1">{contact.email}</Typography>
              </Box>
              {contact.title && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Chip label={contact.title} size="small" variant="outlined" />
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<NoteIcon />}
              onClick={() => setNotesDialogOpen(true)}
            >
              Add Note
            </Button>
            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={() => setAccountDialogOpen(true)}
            >
              Connect Account
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/contacts')}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="contact tabs">
            <Tab label="Overview" {...a11yProps(0)} />
            <Tab label="Activities" {...a11yProps(1)} />
            <Tab label="AI Insights" {...a11yProps(2)} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Overview Tab */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Contact Information
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText primary="Email" secondary={contact.email} />
                    </ListItem>
                    {contact.title && (
                      <ListItem>
                        <ListItemIcon>
                          <PersonIcon />
                        </ListItemIcon>
                        <ListItemText primary="Title" secondary={contact.title} />
                      </ListItem>
                    )}
                    {contactAccount && (
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Account"
                          secondary={
                            <Box>
                              <Typography variant="body2">{contactAccount.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {contactAccount.domain}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemIcon>
                        <CalendarIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Created"
                        secondary={new Date(contact.created_at).toLocaleDateString()}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Stats
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <AssignmentIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Activities"
                        secondary={`${activities.length} recorded`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PsychologyIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="AI Insights"
                        secondary={`${contact.insights?.length || 0} generated`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <NoteIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Notes"
                        secondary={`${contact.notes ? contact.notes.split('\n\n').filter(note => note.trim()).length : 0} recorded`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Notes Section */}
          {contact.notes && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NoteIcon />
                  Notes
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {contact.notes.split('\n\n').filter(note => note.trim()).map((note, index) => {
                    // Parse the note format: [timestamp] subject: content
                    const match = note.match(/^\[([^\]]+)\]\s*([^:]+):\s*(.+)$/);
                    if (match) {
                      const [, timestamp, subject, content] = match;
                      return (
                        <Paper
                          key={index}
                          variant="outlined"
                          sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Typography variant="subtitle2" color="primary">
                              {subject.trim()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {timestamp}
                            </Typography>
                          </Box>
                          <Typography variant="body2">
                            {content.trim()}
                          </Typography>
                        </Paper>
                      );
                    } else {
                      // Fallback for notes that don't match the expected format
                      return (
                        <Paper
                          key={index}
                          variant="outlined"
                          sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}
                        >
                          <Typography variant="body2">
                            {note.trim()}
                          </Typography>
                        </Paper>
                      );
                    }
                  })}
                </Box>
              </CardContent>
            </Card>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Activities Tab */}
          <Typography variant="h6" gutterBottom>
            Recent Activities
          </Typography>
          {activities.length === 0 ? (
            <Alert severity="info">
              No activities recorded for this contact yet.
            </Alert>
          ) : (
            <List>
              {activities.map((activity) => (
                <ListItem key={activity.id} divider>
                  <ListItemIcon>
                    {activity.activity_type === 'EMAIL' ? <EmailIcon /> : <AssignmentIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={activity.subject || activity.activity_type}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {activity.summary || activity.content}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(activity.created_at).toLocaleString()}
                        </Typography>
                        {activity.sentiment && (
                          <Chip
                            label={activity.sentiment}
                            size="small"
                            color={
                              activity.sentiment === 'Positive' ? 'success' :
                                activity.sentiment === 'Negative' ? 'error' : 'default'
                            }
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* AI Insights Tab */}
          <Typography variant="h6" gutterBottom>
            AI-Generated Insights
          </Typography>
          {!contact.insights || contact.insights.length === 0 ? (
            <Alert severity="info">
              No AI insights available for this contact yet.
            </Alert>
          ) : (
            <List>
              {contact.insights.map((insight) => (
                <ListItem key={insight.id} divider>
                  <ListItemIcon>
                    <PsychologyIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={insight.insight_text}
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Generated by {insight.generated_by_agent} • {new Date(insight.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </TabPanel>
      </Paper>

      {/* Edit Contact Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editFormData.first_name}
                onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editFormData.last_name}
                onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={updateContactMutation.isLoading}
          >
            {updateContactMutation.isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={noteSubject}
                onChange={(e) => setNoteSubject(e.target.value)}
                placeholder="Enter note subject..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Note Content"
                multiline
                rows={4}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Enter your note here..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddNote}
            variant="contained"
            disabled={addNoteMutation.isLoading || !noteSubject.trim() || !newNote.trim()}
          >
            {addNoteMutation.isLoading ? 'Adding...' : 'Add Note'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connect Account Dialog */}
      <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Connect to Account</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Select Account</InputLabel>
                <Select
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  label="Select Account"
                >
                  {accounts?.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name} ({account.domain})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {contact.account_id && (
              <Grid item xs={12}>
                <Alert severity="info">
                  This contact is currently connected to: {contact.account?.name || 'Unknown Account'}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConnectAccount}
            variant="contained"
            disabled={connectAccountMutation.isLoading || !selectedAccountId}
          >
            {connectAccountMutation.isLoading ? 'Connecting...' : 'Connect Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
